import React, { useState, useEffect, useRef } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button, Table, TableBody, TextField, TableCell, TableContainer, TableHead, MenuItem,
    TableRow, Paper, Switch, Dialog, DialogTitle, DialogActions, DialogContent, List, ListItem, ListItemText, Collapse
} from '@mui/material';
import { Note } from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import axios from 'axios';
import { fetchSiteData, fetchFaultData } from '../utils/dataLoader';
import { collection, addDoc, onSnapshot, doc } from "firebase/firestore";
import { db } from "../utils/firebase";
import { Brightness3, WbSunny, Save as SaveIcon, ViewList as ViewListIcon, Sort as SortIcon } from '@mui/icons-material';

import CircleIcon from '@mui/icons-material/Circle';
import InfoIcon from '@mui/icons-material/Info';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { CSSTransition } from 'react-transition-group';

import './styleSheets/SiteDashboard.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const SiteDashboard = () => {
    const { siteName: paramSiteName } = useParams();
    const siteName = paramSiteName;
    const [siteData, setSiteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const navigate = useNavigate();
    const [clickCount, setClickCount] = useState(0);

    const [impactedKW, setImpactedKW] = useState(0);
    const [energyRate, setEnergyRate] = useState(0.42);
    const [kWPerPanel, setKWPerPanel] = useState(0.4);

    const [pvWattsData, setPVWattsData] = useState(null);
    const [pvWattsFetched, setPvWattsFetched] = useState(false);
    const lastImpactedKW = useRef(null);
    const [expandedLevel, setExpandedLevel] = useState(null);
    const [isInfoHovered, setIsInfoHovered] = useState(false);

    const [faultRevenueData, setFaultRevenueData] = useState([]);
    const [kWhLost, setKWhLost] = useState(0);
    const [panelsAffected, setPanelsAffected] = useState(0);
    const [revenueLost, setRevenueLost] = useState(0);
    const [error, setError] = useState(null);

    const [exportOpen, setExportOpen] = useState(false);
    const [fileName, setFileName] = useState(`inverterBlock00000000`);
    const [exportFormat, setExportFormat] = useState('csv');

    const [isVisualMode, setIsVisualMode] = useState(true);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipData, setTooltipData] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const inverterBlocks = siteData?.inverterBlocks || {};
    const tableData = Object.values(inverterBlocks).map(({ inverterBlock, totalPanels, affectedPanels }) => ({
        inverterBlock,
        totalPanels,
        affectedPanels,
        percentageAffected: totalPanels > 0 ? ((affectedPanels / totalPanels) * 100).toFixed(2) : "0.00",
    }));

    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [newNoteContent, setNewNoteContent] = useState("");

    const [hasTimeout, setHasTimeout] = useState(false);

    const PVWATTS_API_URL = 'https://developer.nrel.gov/api/pvwatts/v6.json';
    const PVWATTS_API_KEY = process.env.REACT_APP_PVWATTS_API_KEY;

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await fetchSiteData(siteName);

                setSiteData(data);
                setImpactedKW(data.impactedKW || 0);
                setSelectedDate(data.dates[0]?.id || null);

                setKWPerPanel(data.customFields?.panelProd || 0.4);
                setEnergyRate(data.customFields?.kwValue || 0.42);

                console.log("Fetched Site Data:", data);
                console.log("Impacted KW:", data.impactedKW);

                setLoading(false);
            } catch (error) {
                console.error('Error fetching site data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, [siteName]);



    useEffect(() => {
        if (!siteName) return;

        const siteDocRef = doc(db, "sites", siteName);

        const unsubscribe = onSnapshot(siteDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                setKWPerPanel(data.customFields?.panelProd || 0.5);
                setEnergyRate(data.customFields?.kwValue || 0.12);
            }
        });

        return () => unsubscribe();
    }, [siteName]);

    useEffect(() => {
        const fetchRevenueData = async () => {
            try {
                const faults = await fetchFaultData(siteName, selectedDate);
                if (!faults) throw new Error("No fault data available.");

                const faultMap = {};

                Object.values(faults).forEach((fault) => {
                    const faultId = fault.ID || "Unknown";

                    if (!faultMap[faultId]) {
                        faultMap[faultId] = {
                            faultId,
                            affectedPanels: 0,
                            lat: fault.latitude,
                            lng: fault.longitude,
                        };
                    }

                    faultMap[faultId].affectedPanels += 1;
                });

                const processedFaults = Object.values(faultMap).map((fault) => {
                    const faultKW = fault.affectedPanels * kWPerPanel;
                    const faultRevenueLoss = faultKW * energyRate * 365;

                    return {
                        faultId: fault.faultId,
                        affectedPanels: fault.affectedPanels,
                        faultKW,
                        faultRevenueLoss,
                        lat: fault.lat,
                        lng: fault.lng,
                    };
                });

                processedFaults.sort((a, b) => b.faultRevenueLoss - a.faultRevenueLoss);
                setFaultRevenueData(processedFaults);

                const totalPanelsAffected = processedFaults.reduce((sum, fault) => sum + fault.affectedPanels, 0);
                setPanelsAffected(totalPanelsAffected);

                const impactedKW = totalPanelsAffected * kWPerPanel;
                const pvData = await fetchPVWattsData(
                    processedFaults[0]?.lat,
                    processedFaults[0]?.lng,
                    impactedKW
                );

                if (pvData) {
                    const annualKWhLost = pvData.ac_annual.toFixed(2);
                    setKWhLost(annualKWhLost);
                    setRevenueLost((annualKWhLost * energyRate).toFixed(2));
                }

                setLoading(false);
            } catch (error) {
                console.error("Error fetching revenue data:", error);
                setError("Failed to fetch revenue data");
                setLoading(false);
            }
        };

        fetchRevenueData();
    }, [siteName, selectedDate, kWPerPanel, energyRate]);

    useEffect(() => {
        if (!siteData) {
            const timer = setTimeout(() => {
                setHasTimeout(true);
            }, 300000); // 300000 ms = 5 minutes
            return () => clearTimeout(timer);
        } else {
            // If loading is false, reset the timeout flag
            setHasTimeout(false);
        }
    }, [siteData]);

    useEffect(() => {
        const calculatePVWatts = async () => {
            if (!siteData || impactedKW === 0 || pvWattsData) return;
            if (pvWattsFetched && lastImpactedKW.current === impactedKW) return;

            const { latitude: lat, longitude: lng } = siteData.location || {};
            if (!lat || !lng) {
                console.error("Latitude and longitude are missing from siteData.location.");
                return;
            }

            console.log("Calculating PVWatts with impactedKW:", impactedKW);
            const pvData = await fetchPVWattsData(lat, lng, impactedKW);
            if (pvData) {
                setPVWattsData(pvData);
                setSiteData(prevData => ({
                    ...prevData,
                    kWhLost: pvData.ac_annual || 0,
                    revenueLost: ((pvData.ac_annual || 0) * energyRate).toFixed(2),
                }));
                lastImpactedKW.current = impactedKW;
                setPvWattsFetched(true);
            }
        };

        if (siteData && impactedKW > 0) {
            calculatePVWatts();
        }

    }, [siteData, impactedKW, selectedDate, energyRate, kWPerPanel]);



    useEffect(() => {
        if (!siteName) return;
        const siteDocRef = doc(db, "sites", siteName);
        const unsubscribe = onSnapshot(siteDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                setKWPerPanel(data.customFields?.panelProd || 0.4);
                setEnergyRate(data.customFields?.kwValue || 0.02);
                setPvWattsFetched(false);
            }
        });
        return () => unsubscribe();
    }, [siteName]);



    useEffect(() => {
        console.log('Click count:', clickCount);
        if (clickCount > 0) {
            const timer = setTimeout(() => {
                console.log("Resetting click count due to timeout");
                setClickCount(0);
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [clickCount]);

    useEffect(() => {
        console.log('Click count:', clickCount);

        if (clickCount === 3) {
            const metricItems = document.querySelectorAll('.metric-item');
            metricItems.forEach((item, index) => {
                console.log(`Adding wave and wave-delay-${index} to item`);
                item.classList.add('wave', `wave-delay-${index}`);
            });

            setTimeout(() => {
                metricItems.forEach((item, index) => {
                    console.log(`Removing wave and wave-delay-${index} from item`);
                    item.classList.remove('wave');
                    item.classList.remove(`wave-delay-${index}`);
                });
                setClickCount(0); // Reset click count
            }, 1000);
        }
    }, [clickCount]);


    const handleSectionClick = () => {
        setClickCount(prevCount => prevCount + 1);
    };

    const formatNumberWithCommas = (number) => {
        return new Intl.NumberFormat('en-US').format(number);
    };

    const formatDate = (dateString) => {
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        if (typeof dateString === 'string' && dateString.length === 8) {
            const month = parseInt(dateString.slice(0, 2), 10) - 1;
            const day = parseInt(dateString.slice(2, 4), 10);
            const year = parseInt(dateString.slice(4), 10);
            return `${monthNames[month]} ${day}, ${year}`;
        } else {
            return dateString;
        }
    };

    const generatePieData = () => {
        if (!siteData || !siteData.faultsByDate[selectedDate]) return null;
        const faults = siteData.faultsByDate[selectedDate];
        const categories = faults.reduce((acc, fault) => {
            acc[fault.category] = (acc[fault.category] || 0) + 1;
            return acc;
        }, {});

        return {
            labels: Object.keys(categories),
            datasets: [
                {
                    data: Object.values(categories),
                    backgroundColor: ['#ff6384', '#36a2eb', '#ffce56', '#4bc0c0', '#9966ff', '#f56942'],
                    hoverOffset: 4,
                },
            ],
        };
    };

    const fetchPVWattsData = async (lat, lng, systemSize) => {
        try {
            const response = await axios.get(PVWATTS_API_URL, {
                params: {
                    api_key: PVWATTS_API_KEY,
                    lat: lat,
                    lon: lng,
                    system_capacity: systemSize,
                    module_type: 0,
                    losses: 10,
                    array_type: 2,
                    tilt: 0,
                    azimuth: 180,
                },
            });
            return response.data.outputs;
        } catch (error) {
            console.error('Error fetching PVWatts data:', error);
            return null;
        }
    };

    const getFilteredLevelsData = () => {
        if (!siteData || !siteData.faultsByDate || !selectedDate) return [];

        const faults = siteData.faultsByDate[selectedDate] || [];
        const levelsMap = { "Level 1": [], "Level 2": [], "Level 3": [] };

        faults.forEach((fault) => {
            const { level, category, panelCount = 1 } = fault;

            if (!levelsMap[level]) return;

            let categoryEntry = levelsMap[level].find(cat => cat.categoryName === category);
            if (categoryEntry) {
                categoryEntry.affectedPanels += panelCount;
            } else {
                levelsMap[level].push({
                    categoryName: category,
                    affectedPanels: panelCount,
                });
            }
        });

        return Object.entries(levelsMap).map(([level, categories]) => ({
            level,
            categories,
        }));
    };


    const getAffectedPercentage = ({ categories }) => {
        if (!siteData || !siteData.totalPanels) return { affectedPanels: 0, percentOfSite: 0 };

        let affectedPanels = categories.reduce((sum, { affectedPanels }) => sum + affectedPanels, 0);
        let percentOfSite = ((affectedPanels / siteData.totalPanels) * 100).toFixed(2);

        return { affectedPanels, percentOfSite };
    };

    const exportTableData = (format) => {
        if (!sortedTableData.length) {
            console.error("No data to export.");
            return;
        }

        if (format === "csv") {
            const csvRows = [];
            const headers = ["Inverter Block", "Total Panels", "Affected Panels", "% Affected"];
            csvRows.push(headers.join(","));

            sortedTableData.forEach(row => {
                csvRows.push([
                    row.inverterBlock,
                    row.totalPanels,
                    row.affectedPanels,
                    row.percentageAffected
                ].join(","));
            });

            const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "InverterBlockData.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        if (format === "json") {
            const jsonContent = JSON.stringify(sortedTableData, null, 2);
            const blob = new Blob([jsonContent], { type: "application/json" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "InverterBlockData.json";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const toggleExpandLevel = (level) => {
        setExpandedLevel((prevLevel) => (prevLevel === level ? null : level));
    };

    const handleMouseMove = (event, row) => {
        setTooltipPosition({ top: event.clientY - 20, left: event.clientX + 10 });
        setTooltipData(row);
    };

    const handleToggleTooltip = () => {
        setTooltipVisible(!tooltipVisible);
    };
    const calculateCircles = (total, affected) => {
        const totalCircles = 50;
        let redCircles = Math.round((affected / total) * totalCircles);
        if (affected > 0 && redCircles === 0) {
            redCircles = 1;
        }
        return {
            redCircles,
            greenCircles: totalCircles - redCircles
        };
    };
    const sortedTableData = [...tableData].sort((a, b) => {
        const numA = parseInt(a.inverterBlock.replace("IB", ""), 10);
        const numB = parseInt(b.inverterBlock.replace("IB", ""), 10);

        return numA - numB;
    });

    const exportToCSV = () => {
        const csvContent = [
            ['Inverter Block', 'Total Panels', 'Affected Panels', '% Affected'],
            ...sortedTableData.map(row => [row.inverterBlock, row.totalPanels, row.affectedPanels, row.percentageAffected])
        ].map(e => e.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportToSQL = () => {
        const sqlStatements = sortedTableData.map(row =>
            `INSERT INTO inverter_blocks (inverter_block, total_panels, affected_panels, percentage_affected) 
             VALUES ('${row.inverterBlock}', ${row.totalPanels}, ${row.affectedPanels}, ${row.percentageAffected});`
        ).join("\n");

        const blob = new Blob([sqlStatements], { type: 'text/sql' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.sql`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const exportToJSON = () => {
        const jsonData = sortedTableData.map(row => ({
            inverterBlock: row.inverterBlock,
            totalPanels: row.totalPanels,
            affectedPanels: row.affectedPanels,
            percentageAffected: row.percentageAffected
        }));

        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const handleExport = () => {
        if (exportFormat === 'csv') {
            exportToCSV();
        } else if (exportFormat === 'sql') {
            exportToSQL();
        } else if (exportFormat === 'json') {
            exportToJSON();
        }
        setExportOpen(false);
    };

    useEffect(() => {
        if (selectedDate) {
            setFileName(`inverterBlock${selectedDate}`);
        }
    }, [selectedDate]);

    const handleAddNote = async () => {
        if (!newNoteContent.trim()) return;

        try {
            const noteData = {
                author: "Jiachen Zhao - Developer",
                content: newNoteContent,
                timestamp: new Date().toLocaleString("en-US", { timeZone: "UTC" }),
            };

            const siteNotesRef = collection(db, "sites", siteName, "SiteNotes");
            await addDoc(siteNotesRef, noteData);
            setSiteData(prevData => ({
                ...prevData,
                notes: [...prevData.notes, noteData]
            }));

            setNewNoteContent("");
            setNotesModalOpen(false);
        } catch (error) {
            console.error("Error adding note:", error);
        }
    };


    if (loading) {
        return (
            <div className="page-layout">
                <div className="dashboard-container">
                    <div className="dashboard-main">

                        {/* Skeleton Header */}
                        <div className="dashboard-header-container">
                            <div className="dashboard-header skeleton-box" style={{ height: "200px", marginBottom: "20px" }}></div>
                        </div>

                        {/* Skeleton Grid */}
                        <div className="dashboard-grid">

                            {/* Pie Chart Skeleton */}
                            <div className="dashboard-pie-chart skeleton-box" style={{ height: "250px" }}></div>

                            {/* Site Data Skeleton */}
                            <div className="dashboard-site-data skeleton-box" style={{ height: "300px" }}></div>

                            {/* Fault Level Breakdown Skeleton */}
                            <div className="dashboard-fault-level-card skeleton-box" style={{ height: "350px" }}></div>

                            {/* Data Table Skeleton */}
                            <div className="dashboard-data-table skeleton-box" style={{ height: "300px" }}></div>

                        </div>
                    </div>
                </div>
            </div>
        );

    }

    if (!siteData) {
        if (hasTimeout) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    textAlign: 'center',
                    backgroundColor: '#001501'

                }}>
                    <p style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#fffbed' }}>
                        Whoops... Looks like the data isn't loading right, please refresh
                    </p>
                    <p style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#fffbed' }}>
                        If the issue persists, it may be on our end and we're already working on fixing it
                    </p>
                </div>
            );
        }
        return (
            <div className="page-layout">
                <div className="dashboard-container">
                    <div className="dashboard-main">

                        {/* Skeleton Header */}
                        <div className="dashboard-header-container">
                            <div className="dashboard-header skeleton-box" style={{ height: "200px", marginBottom: "20px" }}></div>
                        </div>

                        {/* Skeleton Grid */}
                        <div className="dashboard-grid">

                            {/* Pie Chart Skeleton */}
                            <div className="dashboard-pie-chart skeleton-box" style={{ height: "250px" }}></div>

                            {/* Site Data Skeleton */}
                            <div className="dashboard-site-data skeleton-box" style={{ height: "300px" }}></div>

                            {/* Fault Level Breakdown Skeleton */}
                            <div className="dashboard-fault-level-card skeleton-box" style={{ height: "350px" }}></div>

                            {/* Data Table Skeleton */}
                            <div className="dashboard-data-table skeleton-box" style={{ height: "300px" }}></div>

                        </div>
                    </div>
                </div>
            </div>
        );

    }

    return (
        <div className="page-layout">

            <div className="dashboard-container">
                <div className="dashboard-main">

                    <div className="dashboard-header-container">
                        <div className="dashboard-header">
                            <h2>Overview</h2>
                            <h1> {siteName} </h1>

                            <div className="date-selection">
                                <label className="date-dropdown">Analysis Date: </label>
                                <select
                                    className="date-dropdown-menu"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                >
                                    {siteData.dates.map((date) => (
                                        <option key={date.id} value={date.id}>
                                            {formatDate(date.id)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <p>
                                Total Panels Installed: {formatNumberWithCommas(siteData.totalPanels)}
                            </p>

                            <p>
                                Site Production:<strong> {
                                    siteData?.totalPanels
                                        ? ((siteData.totalPanels * kWPerPanel) / 1000).toFixed(2)
                                        : "Loading..."
                                } MW </strong>
                            </p>


                        </div>

                        <div className="dashboard-notes-card">
                            <h2>Site Notes</h2>

                            {/* Notes List */}
                            <div className="notes-container">
                                {siteData.notes
                                    .slice()
                                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                                    .map((note, index) => (
                                        <div key={index} className="note-entry">
                                            <p className="note-author">{note.author}</p>
                                            <p className="note-content">{note.content}</p>
                                            <p className="note-timestamp">{note.timestamp}</p>
                                        </div>
                                    ))
                                }
                            </div>

                            {/* Add Note Button */}
                            <Button
                                variant="contained"
                                style={{ backgroundColor: "#285335", color: "white", marginTop: "10px" }}
                                onClick={() => setNotesModalOpen(true)}
                                startIcon={<Note />}
                            >
                                Add Note
                            </Button>

                            {/* Add Note Modal */}
                            <Dialog open={notesModalOpen} onClose={() => setNotesModalOpen(false)}>
                                <DialogContent>
                                    <h3>Add a Note</h3>
                                    <TextField
                                        label="Your Note"
                                        multiline
                                        rows={3}
                                        value={newNoteContent}
                                        onChange={(e) => setNewNoteContent(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                    />
                                    <Button
                                        onClick={handleAddNote}
                                        variant="contained"
                                        style={{ backgroundColor: "#285335", color: "white", marginTop: "10px" }}
                                    >
                                        Save Note
                                    </Button>
                                </DialogContent>
                            </Dialog>
                        </div>
                    </div>

                    {/*Actual dashboard part */}
                    {/*Actual dashboard part */}
                    {/*Actual dashboard part */}
                    {/*Actual dashboard part */}

                    <div className="dashboard-grid">
                        {/* Pie Chart Section */}
                        {/* Pie Chart Section */}
                        {/* Pie Chart Section */}
                        {/* Pie Chart Section */}
                        <div className="dashboard-pie-chart">
                            <h2>Fault Distribution</h2>
                            {generatePieData() ? <Pie data={generatePieData()} /> : <p>No faults available</p>}
                        </div>

                        {/*Site Data tile card */}
                        {/*Site Data tile card */}
                        {/*Site Data tile card */}
                        {/*Site Data tile card */}
                        {/*Site Data tile card */}
                        <div className="dashboard-site-data" onClick={handleSectionClick}>
                            <h2>Site Data</h2>

                            <div className="metric-item metric-highlight-red">
                                <p className="kWh-lost">Estimated Annual kWh Lost</p>
                                <p className="metric-value">
                                    {parseFloat(kWhLost).toLocaleString()} kWh lost annually
                                </p>
                            </div>

                            <div className="metric-item metric-highlight-red">
                                <p className="revenue-lost">Estimated Annual Revenue Lost:</p>
                                <p className="metric-value">
                                    ${parseFloat(revenueLost).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </p>
                            </div>

                            <div className="metric-row">
                                <div className="metric-item metric-highlight-red">
                                    <p className="kwh-lost">Total Panels Affected</p>
                                    <p className="metric-value">{formatNumberWithCommas(siteData.totalPanelsAffected)}</p>
                                </div>
                                <div className="metric-item metric-highlight-red">
                                    <p>Impacted kW</p>
                                    <p className="metric-value">{formatNumberWithCommas(impactedKW)} kW</p>
                                </div>
                            </div>

                            <div className="metric-item metric-highlight-red">
                                <p className="percent-affected">Percent Affected</p>
                                <p className="metric-value">
                                    {siteData.percentAffected ? `${siteData.percentAffected.toFixed(2)}%` : 'Calculating...'}
                                </p>
                            </div>

                            <div className="metric-item metric-combined-orange">
                                <p className="site-location">Site Location</p>
                                <p>{siteData.location?.latitude}, {siteData.location?.longitude}</p>

                                <p className="total-panels">Total Panels</p>
                                <p>{formatNumberWithCommas(siteData.totalPanels)}</p>

                                <p className="total-faults">Total Faults</p>
                                <p>
                                    {siteData.totalFaults
                                        ? formatNumberWithCommas(siteData.totalFaults)
                                        : 'Calculating...'}
                                </p>
                            </div>

                            <p style={{ fontSize: '0.8em', color: 'gray', marginTop: '10px' }}>
                                *Important: These are approximate figures from PVWatts simulations and might vary from actual performance.
                            </p>
                        </div>



                        {/* Fault Level Breakdown Section */}
                        {/* Fault Level Breakdown Section */}
                        {/* Fault Level Breakdown Section */}
                        {/* Fault Level Breakdown Section */}
                        {/* Fault Level Breakdown Section */}
                        <div className="dashboard-fault-level-card">
                            <h2>
                                Fault Level Breakdown
                                <span className="info-icon-container">
                                    <span
                                        className="info-icon"
                                        title="Hover for more info"
                                        onMouseEnter={() => setIsInfoHovered(true)}
                                        onMouseLeave={() => setIsInfoHovered(false)}
                                    >
                                        <InfoIcon fontSize="small" color="action" />
                                    </span>
                                </span>

                            </h2>

                            <div className="levels-summary-vertical">
                                {isInfoHovered ? (
                                    <div className="info-content">
                                        <h3>Failure Definition and Impact</h3>

                                        {/* Level Descriptions */}
                                        {["Level 1", "Level 2", "Level 3"].map((level, index) => (
                                            <div className="info-level" key={index}>
                                                <h4>{level}</h4>
                                                <table>
                                                    <tbody>
                                                        {level === "Level 1" && (
                                                            <>
                                                                <tr><td>String Failed</td><td>String</td></tr>
                                                                <tr><td>Combiner Field</td><td>Field</td></tr>
                                                                <tr><td>Inverter Failed</td><td>Inverter</td></tr>
                                                                <tr><td>Shading</td><td>String</td></tr>
                                                                <tr><td>Multiple Hot Cells</td><td>Panel</td></tr>
                                                            </>
                                                        )}
                                                        {level === "Level 2" && (
                                                            <>
                                                                <tr><td>Tracker Not Aligned</td><td>Tracker Field</td></tr>
                                                                <tr><td>Soiling</td><td>String</td></tr>
                                                                <tr><td>Glass Broken</td><td>Panel</td></tr>
                                                                <tr><td>Broken Module</td><td>Module</td></tr>
                                                                <tr><td>Temp Delta 10 Deg C</td><td>Panel</td></tr>
                                                            </>
                                                        )}
                                                        {level === "Level 3" && (
                                                            <>
                                                                <tr><td>Module Hotspot</td><td>Module</td></tr>
                                                                <tr><td>Module Mult. Hotspot</td><td>Module</td></tr>
                                                                <tr><td>Module Diode Issue</td><td>Module</td></tr>
                                                                <tr><td>Module Missing</td><td>Module</td></tr>
                                                            </>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    getFilteredLevelsData().map(({ level, categories }) => {
                                        const { affectedPanels, percentOfSite } = getAffectedPercentage({ categories });

                                        let color = "yellow";
                                        if (percentOfSite > 0.5 && percentOfSite < 2.5) color = "orange";
                                        else if (percentOfSite >= 2.5) color = "red";

                                        return (
                                            <div
                                                key={level}
                                                className={`level-box ${color} ${expandedLevel === level ? "expanded" : ""}`}
                                                onClick={() => toggleExpandLevel(level)}
                                            >
                                                <h3>{level}</h3>
                                                <p>{affectedPanels} panels affected</p>
                                                <p className="small-text">{percentOfSite}% of site</p>

                                                <div className="expanded-content">
                                                    {expandedLevel === level && (
                                                        <TableContainer component={Paper}>
                                                            <Table>
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Category</TableCell>
                                                                        <TableCell>Panels Affected</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {categories.map(({ categoryName, affectedPanels }, index) => (
                                                                        <TableRow key={index}>
                                                                            <TableCell>{decodeURIComponent(categoryName)}</TableCell>
                                                                            <TableCell>{affectedPanels}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/*dots and tables*/}
                        {/*dots and tables*/}

                        {/*dots and tables*/}
                        {/*dots and tables*/}
                        {/*dots and tables*/}
                        {/*dots and tables*/}

                        <div className="dashboard-data-table" style={{ maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                            <h2>Block Data</h2>
                            <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '10px' }}>
                                {/* Export Button */}
                                <Button
                                    onClick={() => setExportOpen(true)}
                                    variant="outlined"
                                    sx={{

                                        top: '10px',
                                        right: '20px',
                                        bottom: '15px',
                                        color: '#285335',
                                        borderColor: '#285335',
                                        '&:hover': { color: '#1e4029' }
                                    }}
                                ><FileDownloadIcon /></Button>
                                {/*Toggle */}
                                <Button
                                    onClick={() => setIsVisualMode(!isVisualMode)}
                                    startIcon={isVisualMode ? <TableRowsIcon /> : <CircleIcon />}
                                    variant="outlined"
                                    sx={{
                                        top: '10px',
                                        right: '10px',
                                        bottom: '15px',
                                        color: '#285335',
                                        borderColor: '#285335',
                                        '&:hover': { color: '#1e4029' }
                                    }}
                                >
                                    {isVisualMode ? "Table" : "Dots"}
                                </Button>

                            </div>

                            {/* Render the tooltip element */}
                            {tooltipVisible && tooltipData && (
                                <div
                                    className="row-tooltip"
                                    style={{ top: `${tooltipPosition.top}px`, left: `${tooltipPosition.left}px` }}
                                >
                                    <p><strong>{tooltipData.inverterBlock}</strong></p>
                                    <p>Total Panels: {tooltipData.totalPanels}</p>
                                    <p>Affected Panels: {tooltipData.affectedPanels}</p>
                                    <p>% Affected: {tooltipData.percentageAffected}%</p>
                                </div>
                            )}

                            <div onClick={handleToggleTooltip}>

                                {/*Dots View */}
                                <Collapse in={isVisualMode}>
                                    <div key="circleView">
                                        {sortedTableData.map((row, index) => (
                                            <div
                                                key={index}
                                                className="visual-row"
                                                onMouseMove={(e) => handleMouseMove(e, row)}
                                                onMouseLeave={() => setTooltipData(null)}
                                            >
                                                <span className="inverter-block-label">
                                                    {`Block ${row.inverterBlock.replace("IB", "")}`}
                                                </span>
                                                <div className="circle-row">
                                                    {Array.from({ length: calculateCircles(row.totalPanels, row.affectedPanels).redCircles }, (_, i) => (
                                                        <span key={`red-${i}`} className="circle red-circle" />
                                                    ))}
                                                    {Array.from({ length: calculateCircles(row.totalPanels, row.affectedPanels).greenCircles }, (_, i) => (
                                                        <span key={`green-${i}`} className="circle green-circle" />
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Collapse>

                                {/*table view */}
                                <Collapse in={!isVisualMode}>
                                    <TableContainer component={Paper} key="tableView">
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Block</TableCell>
                                                    <TableCell>Total Panels</TableCell>
                                                    <TableCell>Affected Panels</TableCell>
                                                    <TableCell>% Affected</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sortedTableData.map((row, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{row.inverterBlock.replace("IB", "")}</TableCell>
                                                        <TableCell>{row.totalPanels}</TableCell>
                                                        <TableCell>{row.affectedPanels}</TableCell>
                                                        <TableCell>{row.percentageAffected}%</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Collapse>
                            </div>

                            {/*Export prompt */}
                            <Dialog
                                open={exportOpen}
                                onClose={() => setExportOpen(false)}
                                sx={{
                                    "& .MuiDialog-paper": {
                                        backgroundColor: "#fffbed",
                                        borderRadius: "10px",
                                        padding: "20px",
                                        width: "350px",
                                        border: "3px solid #333"
                                    }
                                }}
                            >
                                <DialogContent>
                                    <h3 style={{ textAlign: "center", color: "#285335", fontSize: "1.2em", marginBottom: "10px" }}>
                                        Export Data
                                    </h3>
                                    <TextField
                                        label="File Name"
                                        value={fileName}
                                        onChange={(e) => setFileName(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                    />
                                    <TextField
                                        select
                                        label="Export Format"
                                        value={exportFormat}
                                        onChange={(e) => setExportFormat(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                    >
                                        <MenuItem value="csv">CSV (.csv)</MenuItem>
                                        <MenuItem value="sql">SQL (.sql)</MenuItem>
                                        <MenuItem value="json">JSON (.json)</MenuItem>
                                    </TextField>
                                    <Button
                                        onClick={handleExport}
                                        variant="contained"
                                        sx={{
                                            backgroundColor: "#285335",
                                            color: "white",
                                            width: "100%",
                                            marginTop: "10px",
                                            "&:hover": { backgroundColor: "#1e4027" }
                                        }}
                                    >
                                        Export
                                    </Button>
                                </DialogContent>
                            </Dialog>

                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default SiteDashboard;
